import React, { useEffect, useState } from 'react';
import CreateUpdateProductModal from '../modals/CreateUpdateProductModal';
import { useForm } from 'react-hook-form';
import ProductService from '../../../../services/ProductService';
import CustomerService from '../../../../services/CustomersService';
import { toast, ToastContainer } from 'react-toastify';
import SupportTickets from './SupportTickets';
import { priorityOptions, statusOptions } from '../../../../utils/OptionUtils';
import ListExpiredOrder from './ListExpiredOrder';
import ListActiveOrder from './ListActiveOrder';

const ListOrder = ({ products: initialProducts = [], company, customerId, setCustomer }) => {
  const { formState: { errors }, control, handleSubmit, reset, register, setValue } = useForm();
  const [showProductModal, setShowProductModal] = useState(false);
  const [products, setProducts] = useState(initialProducts || []);
  const [productList, setProductList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [view, setView] = useState('products');
  const [listType, setListType] = useState('list');
  const [activeTab, setActiveTab] = useState('active');

  const fetchProductList = async () => {
    try {
      const response = await ProductService.list();
      if (response.status === 200) {
        setProductList(response.data?.data || []);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Failed to fetch product list.');
    }
  };

  useEffect(() => {
    fetchProductList();
  }, []);
  const handleEdit = async (id) => {
    try {
      const response = await CustomerService.getProducts(id);
      if (response.status === 200) {
        setEditingProduct(response.data?.data);
        setShowProductModal(true);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Failed to fetch product details.');
    }
  };

  const openProductCreateModal = () => {
    setEditingProduct(null);
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
  };

  const onSubmit = async (data, productId = null) => {
    setLoading(true);
    try {
      data.customer_id = customerId;
      const response = productId
        ? await CustomerService.updateProducts(productId, { data })
        : await CustomerService.createProducts({ data });

      if (response?.status === 200 || response?.status === 201) {
        const responseData = response.data?.data;
        const message = response.data?.message || (productId ? 'Product updated successfully!' : 'Product created successfully!');

        setProducts((prevProducts) => {
          if (productId) {
            return prevProducts.map((product) =>
              product.id === productId ? { ...responseData.product, ...responseData } : product
            );
          } else {
            return Array.isArray(responseData) && responseData.length
              ? [...prevProducts, ...responseData.map((item) => ({ ...item.product, ...item }))]
              : prevProducts;
          }

        });
        setCustomer((prevCustomer) => ({
          ...prevCustomer,
          products: Array.isArray(responseData) && responseData.length
            ? [...prevCustomer.products, ...responseData.map((item) => ({ ...item.product, ...item }))]
            : prevCustomer.products,
        }));

        toast.success(message);
        closeProductModal();
        reset();
        setActiveTab('active');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Failed to process the request.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialProducts.length) {
      const formattedProducts = initialProducts.map((item) => ({ ...item.product, ...item }));
      setProducts(formattedProducts);
    }
  }, [initialProducts]);

  const toggleView = (newView) => {
    setView(newView);
  };

  const openSupportTicketForm = () => {
    setListType('form');
  };

  return (
    <>
      <div className="container-fluid">
        <div className="card row">
          <div className="card-header">
            <div className="row align-items-center">
              <div className="col">
                <h2 className="header-title">{company}</h2>
              </div>
              <div className="col-auto">
                <button
                  className="btn btn-primary btn-sm text-white me-2"
                  onClick={() => {
                    if (view === 'products') {
                      openProductCreateModal();
                    } else if (view === 'supports') {
                      openSupportTicketForm();
                    } else {
                      toggleView('products');
                    }
                  }}
                >
                  Add More
                </button>
                {view !== 'products' && (
                  <button
                    className="btn btn-primary btn-sm text-white"
                    onClick={() => toggleView('products')}
                  >
                    Back
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="row align-items-center">
            { view === 'products' && (
              <div className="col">
              <ul className="nav nav-tabs nav-tabs-sm nav-justified card-header-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'active' ? 'active text-primary' : ''}`}
                    onClick={() => setActiveTab('active')}
                  >
                    Active Order
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'expired' ? 'active text-primary' : ''}`}
                    onClick={() => setActiveTab('expired')}
                  >
                    Expired Order
                  </button>
                </li>
              </ul>
            </div>
          )}
            <div className="table-responsive">
              {activeTab === 'active' && view === 'products'? (
                <ListActiveOrder
                  products={products}
                  loading={loading}
                  handleEdit={handleEdit}
                  toggleView={toggleView}
                />
              ) : (
                activeTab === 'expired' && view === 'products' && (
                  <ListExpiredOrder
                  products={products}
                  loading={loading}
                  handleEdit={handleEdit}
                  toggleView={toggleView}
                  onSubmit={onSubmit}
                  />
                )
              )}
              {view === 'supports' && (
                <SupportTickets
                  customerId={customerId}
                  priorityOptions={priorityOptions}
                  statusOptions={statusOptions}
                  productOptions={productList}
                  listType={listType}
                  setListType={setListType}
                  reset={reset}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {showProductModal && (
        <CreateUpdateProductModal
          show={showProductModal}
          handleClose={closeProductModal}
          onSubmit={onSubmit}
          handleSubmit={handleSubmit}
          control={control}
          errors={errors}
          register={register}
          setValue={setValue}
          reset={reset}
          productList={productList}
          initialValues={editingProduct}
        />
      )}

      <ToastContainer containerId={'ticket-container'} />
    </>
  );
};

export default ListOrder;
